import React, { useState, useEffect, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import AssignmentFormLayout from "../components/AssignmentFormLayout";
import { AssignmentFormValues, SelectedUser } from "../types";
import { editUserAssignments } from "../../../services/api/apiClient";
import { TrainingRecordMode } from "../../../services/api/types/userAssignment/types";
import { usePortalGroups } from "../hooks/usePortalGroups";
import { useToast } from "../../../context/ToastContext";
import { usePageMetadata } from "../../../context/PageMetadataContext";
import { CombinedSelectedItemsPreview } from "../../../components/SelectedItemsPreview/CombinedSelectionPreview";
import { AssignmentActionToggle } from "../components/AssignmentActionToggle";
import { extractErrorMessage } from "../../../utils/errorUtils";

interface AssignmentData {
  id: string;
  title: string;
  description?: string;
  usersList?: Array<{
    UserPortalUid: string;
    name: string;
    email: string;
  }>;
  dueDate?: string;
  group?: string;
  status?: boolean;
  type?: string;
}

const EditAssignment: React.FC = () => {
  const { id, portalUid } = useParams<{ id: string; portalUid: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const state = (location.state || {}) as {
    assignmentData?: AssignmentData;
    selectedUsers?: SelectedUser[];
    selectedAssignments?: Array<{
      id: string;
      title: string;
      metadata?: {
        group?: string;
        dueDate?: string;
        type?: string;
      };
    }>;
  };

  const assignmentData = state?.assignmentData;
  const selectedUsers = state?.selectedUsers || [];
  const selectedAssignments = state?.selectedAssignments || [];

  const { showToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [openDeadlinePicker, setOpenDeadlinePicker] = useState(false);

  const getGroupValue = (groupName: string | undefined) => {
    if (!groupName) return "";

    if (groupName.includes(":")) {
      return groupName;
    }

    return groupName;
  };

  const initialFormValues: AssignmentFormValues & {
    title?: string;
    description?: string;
  } = {
    group: getGroupValue(assignmentData?.group) || "",
    deadlineDate: assignmentData?.dueDate || "",
    deadlineOffset: "7",
    notify: false,
    setDeadlineToNull: false,
    title: assignmentData?.title,
    description: assignmentData?.description,
    mode: "update",
  };

  const [formValues, setFormValues] = useState<
    AssignmentFormValues & { title?: string; description?: string }
  >(initialFormValues);

  const resolvedUpdateValues = useRef({
    group: initialFormValues.group,
    deadlineDate: initialFormValues.deadlineDate,
    deadlineOffset: initialFormValues.deadlineOffset,
  });

  const {
    groups,
    loading: groupsLoading,
    error: groupsError,
  } = usePortalGroups(portalUid || "");

  useEffect(() => {
    if (assignmentData && groups.length > 0) {
      const findGroupId = (groupName: string): string => {
        if (!groupName) return "";

        if (groupName.includes(":")) {
          const [groupId, subGroupId] = groupName.split(":");
          const group = groups.find((g) => g.id === groupId);
          if (group && subGroupId) {
            const subGroup = group.subGroups.find((sg) => sg.id === subGroupId);
            if (subGroup) {
              return groupName;
            }
          }
          const groupById = groups.find((g) => g.id === groupId);
          if (groupById) return groupId;
        }

        for (const group of groups) {
          const subGroupMatch = group.subGroups.find(
            (sg) => sg.name === groupName,
          );
          if (subGroupMatch) {
            return `${group.id}:${subGroupMatch.id}`;
          }
        }

        const groupMatch = groups.find((g) => g.name === groupName);
        if (groupMatch) {
          return groupMatch.id;
        }

        for (const group of groups) {
          const subGroupMatch = group.subGroups.find(
            (sg) => groupName.includes(sg.name) || sg.name.includes(groupName),
          );
          if (subGroupMatch) {
            return `${group.id}:${subGroupMatch.id}`;
          }
        }

        const partialGroupMatch = groups.find(
          (g) => groupName.includes(g.name) || g.name.includes(groupName),
        );
        if (partialGroupMatch) {
          return partialGroupMatch.id;
        }

        return "";
      };

      const groupId = findGroupId(assignmentData.group || "");

      resolvedUpdateValues.current = {
        group: groupId || "",
        deadlineDate: assignmentData.dueDate || "",
        deadlineOffset: "7",
      };

      setFormValues((prev) => ({
        ...prev,
        mode: "update",
        group: groupId || "",
        deadlineDate: assignmentData.dueDate || "",
        title: assignmentData.title,
        description: assignmentData.description,
      }));
    }
  }, [assignmentData, groups]);

  const handleFormChange = (values: Partial<AssignmentFormValues>) => {
    if (values.mode && values.mode !== formValues.mode) {
      if (values.mode === "create") {
        setFormValues((prev) => ({
          ...prev,
          mode: "create",
          group: "",
          deadlineDate: "",
          deadlineOffset: "7",
          notify: false,
          setDeadlineToNull: false,
        }));
      } else {
        setFormValues((prev) => ({
          ...prev,
          mode: "update",
          group: resolvedUpdateValues.current.group,
          deadlineDate: resolvedUpdateValues.current.deadlineDate,
          deadlineOffset: resolvedUpdateValues.current.deadlineOffset,
          notify: false,
          setDeadlineToNull: false,
        }));
      }
      return;
    }
    setFormValues((prev) => ({ ...prev, ...values }));
  };

  const handleSubmit = async () => {
    if (!portalUid) {
      showToast("Portal ID is required", "error");
      return;
    }

    const isCreate = formValues.mode === "create";

    try {
      setSubmitting(true);

      const portalGroupUid = formValues.group
        ? String(formValues.group).split(":").pop()
        : null;

      const assignments =
        selectedAssignments.length > 0
          ? selectedAssignments.map((a) => ({
              portalAssignmentUid: a.id,
              users: selectedUsers.map((u) => u.UserPortalUid),
            }))
          : id
            ? [
                {
                  portalAssignmentUid: id,
                  users: selectedUsers.map((u) => u.UserPortalUid),
                },
              ]
            : [];

      const requestBody: any = {
        assignments,
        mode: isCreate
          ? TrainingRecordMode.ADD_NEW_TR
          : TrainingRecordMode.EDIT_EXISTING_TR,
        portalGroupUid: portalGroupUid,
      };

      const hasValidDeadlineDate =
        formValues.deadlineDate && formValues.deadlineDate.trim() !== "";
      if (hasValidDeadlineDate) {
        requestBody.dueDate = new Date(formValues.deadlineDate as string)
          .toISOString()
          .split("T")[0];
        requestBody.emptyDueDate = false;

        if (
          formValues.deadlineOffset &&
          formValues.deadlineOffset.trim() !== ""
        ) {
          const plusDays = parseInt(formValues.deadlineOffset, 10);
          if (!isNaN(plusDays) && plusDays >= 0) {
            requestBody.plusDays = plusDays;
          }
        }
      } else {
        requestBody.emptyDueDate = true;
      }

      await editUserAssignments(portalUid, requestBody);

      showToast(
        isCreate
          ? "Training record created successfully!"
          : "Assignment updated successfully!",
        "success",
      );

      setTimeout(() => {
        navigate(`/portal-configuration/${portalUid}/users/manage-assignments`);
      }, 1500);
    } catch (err: any) {
      showToast(
        extractErrorMessage(
          err,
          isCreate
            ? "Error creating training record"
            : "Failed to update assignment. Please try again.",
        ),
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  usePageMetadata(
    {
      title:
        assignmentData && selectedAssignments.length <= 1
          ? `Edit Assignment: ${assignmentData.title}`
          : "Edit assignment",
      breadcrumbs: [
        {
          label: "User management",
          path: `/portal-configuration/${portalUid}/users`,
        },
        {
          label: "User search",
          path: `/portal-configuration/${portalUid}/users`,
        },
        {
          label: "Manage assignments",
          path: `/portal-configuration/${portalUid}/users/manage-assignments`,
        },
        {
          label: "Edit assignment",
        },
      ],
    },
    [portalUid, assignmentData, selectedAssignments.length],
  );

  if (!assignmentData) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">
          No assignment data was provided. Please go back and try again.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <CombinedSelectedItemsPreview
        users={selectedUsers.map((u) => ({
          id: u.UserPortalUid,
          title: `${u.firstName} ${u.lastName}`,
          subtitle: u.email,
        }))}
        assignments={selectedAssignments.map((a) => ({
          id: a.id,
          title: a.title,
        }))}
      />

      <AssignmentActionToggle
        value={formValues.mode}
        onChange={handleFormChange}
      />

      <AssignmentFormLayout
        mode={formValues.mode}
        selectedItems={selectedUsers}
        formValues={{
          group: formValues.group,
          deadlineDate: formValues.deadlineDate,
          deadlineOffset: formValues.deadlineOffset,
          notify: formValues.notify,
          setDeadlineToNull: formValues.setDeadlineToNull,
          mode: formValues.mode,
        }}
        onFormValuesChange={handleFormChange}
        onSubmit={handleSubmit}
        submitButtonText={
          formValues.mode === "update" ? "Update" : "Create new training record"
        }
        itemType="users"
        isSubmitting={submitting}
        openDeadlinePicker={openDeadlinePicker}
        onOpenDeadlinePickerChange={() => setOpenDeadlinePicker(true)}
        onCloseDeadlinePickerChange={() => setOpenDeadlinePicker(false)}
        groupsData={{ groups, loading: groupsLoading, error: groupsError }}
      />
    </Box>
  );
};

export default EditAssignment;
